/**
 * 통합 디자인 시스템 - 테마 설정
 * 
 * Ant Design 테마와 커스텀 스타일을 위한 디자인 토큰 정의
 * 색상, 타이포그래피, 공간, 애니메이션 등의 일관된 디자인 언어를 제공합니다.
 */

// 색상 팔레트
export const colors = {
  // Primary Colors (브랜드 컬러)
  primary: {
    50: '#e6f4ff',
    100: '#bae0ff',
    200: '#91caff',
    300: '#69b1ff',
    400: '#4096ff',
    500: '#1890ff', // 메인 브랜드 컬러
    600: '#096dd9',
    700: '#0050b3',
    800: '#003a8c',
    900: '#002766',
  },
  
  // Secondary Colors
  secondary: {
    50: '#f9f9ff',
    100: '#edecff',
    200: '#ddd8ff',
    300: '#cac1ff',
    400: '#b4a9ff',
    500: '#9c88ff',
    600: '#8b5cf6',
    700: '#7c3aed',
    800: '#6d28d9',
    900: '#5b21b6',
  },
  
  // Status Colors
  success: {
    50: '#f6ffed',
    100: '#d9f7be',
    200: '#b7eb8f',
    500: '#52c41a',
    600: '#389e0d',
    700: '#237804',
  },
  warning: {
    50: '#fffbe6',
    100: '#fff1b8',
    200: '#ffe58f',
    500: '#faad14',
    600: '#d48806',
    700: '#ad6800',
  },
  error: {
    50: '#fff2f0',
    100: '#ffccc7',
    200: '#ffa39e',
    500: '#ff4d4f',
    600: '#cf1322',
    700: '#a8071a',
  },
  info: {
    50: '#e6f4ff',
    100: '#bae0ff',
    200: '#91caff',
    500: '#1890ff',
    600: '#096dd9',
    700: '#0050b3',
  },
  
  // Neutral Colors
  gray: {
    50: '#fafafa',
    100: '#f5f5f5',
    200: '#f0f0f0',
    300: '#d9d9d9',
    400: '#bfbfbf',
    500: '#8c8c8c',
    600: '#595959',
    700: '#434343',
    800: '#262626',
    900: '#141414',
  },
  
  // Background Colors
  background: {
    primary: '#ffffff',
    secondary: '#f5f5f5',
    tertiary: '#fafafa',
    dark: '#001529',
    card: '#ffffff',
    disabled: '#f5f5f5',
  },
  
  // Text Colors
  text: {
    primary: 'rgba(0, 0, 0, 0.88)',
    secondary: 'rgba(0, 0, 0, 0.65)',
    tertiary: 'rgba(0, 0, 0, 0.45)',
    disabled: 'rgba(0, 0, 0, 0.25)',
    inverse: '#ffffff',
    link: '#1890ff',
    success: '#52c41a',
    warning: '#faad14',
    error: '#ff4d4f',
  },
  
  // Border Colors
  border: {
    primary: '#d9d9d9',
    secondary: '#f0f0f0',
    focus: '#1890ff',
    error: '#ff4d4f',
    success: '#52c41a',
  }
};

// 타이포그래피
export const typography = {
  fontFamily: {
    sans: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    mono: 'SFMono-Regular, Consolas, "Liberation Mono", Menlo, Courier, monospace',
  },
  fontSize: {
    xs: '12px',
    sm: '14px',
    base: '16px',
    lg: '18px',
    xl: '20px',
    '2xl': '24px',
    '3xl': '32px',
    '4xl': '40px',
  },
  fontWeight: {
    light: 300,
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
  lineHeight: {
    tight: 1.2,
    normal: 1.5,
    relaxed: 1.8,
  },
};

// 공간 (Spacing)
export const spacing = {
  0: '0',
  1: '4px',
  2: '8px',
  3: '12px',
  4: '16px',
  5: '20px',
  6: '24px',
  8: '32px',
  10: '40px',
  12: '48px',
  16: '64px',
  20: '80px',
  24: '96px',
  32: '128px',
};

// 반응형 브레이크포인트
export const breakpoints = {
  xs: 480,
  sm: 576,
  md: 768,
  lg: 992,
  xl: 1200,
  xxl: 1600,
};

// 그림자
export const shadows = {
  sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  base: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
  md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  card: '0 2px 8px rgba(0, 0, 0, 0.1)',
  modal: '0 8px 24px rgba(0, 0, 0, 0.15)',
};

// 보더 반지름
export const radius = {
  none: '0',
  sm: '4px',
  base: '6px',
  md: '8px',
  lg: '12px',
  xl: '16px',
  full: '50%',
};

// 애니메이션
export const animations = {
  duration: {
    fast: '150ms',
    normal: '300ms',
    slow: '500ms',
  },
  easing: {
    easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
    easeOut: 'cubic-bezier(0, 0, 0.2, 1)',
    easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
  },
};

// Ant Design 테마 설정
export const antdTheme = {
  token: {
    // 색상
    colorPrimary: colors.primary[500],
    colorSuccess: colors.success[500],
    colorWarning: colors.warning[500],
    colorError: colors.error[500],
    colorInfo: colors.info[500],
    
    // 배경
    colorBgContainer: colors.background.primary,
    colorBgElevated: colors.background.card,
    colorBgLayout: colors.background.secondary,
    
    // 텍스트
    colorText: colors.text.primary,
    colorTextSecondary: colors.text.secondary,
    colorTextTertiary: colors.text.tertiary,
    colorTextDisabled: colors.text.disabled,
    
    // 보더
    colorBorder: colors.border.primary,
    colorBorderSecondary: colors.border.secondary,
    
    // 타이포그래피
    fontFamily: typography.fontFamily.sans,
    fontSize: 14,
    fontSizeHeading1: 32,
    fontSizeHeading2: 24,
    fontSizeHeading3: 20,
    fontSizeHeading4: 16,
    fontSizeHeading5: 14,
    
    // 공간
    padding: 16,
    paddingXS: 8,
    paddingSM: 12,
    paddingLG: 24,
    paddingXL: 32,
    
    margin: 16,
    marginXS: 8,
    marginSM: 12,
    marginLG: 24,
    marginXL: 32,
    
    // 보더 반지름
    borderRadius: 6,
    borderRadiusSM: 4,
    borderRadiusLG: 8,
    
    // 애니메이션
    motionDurationFast: animations.duration.fast,
    motionDurationMid: animations.duration.normal,
    motionDurationSlow: animations.duration.slow,
    
    // 레이아웃
    controlHeight: 32,
    controlHeightSM: 24,
    controlHeightLG: 40,
    
    // 그림자
    boxShadow: shadows.base,
    boxShadowSecondary: shadows.sm,
    boxShadowTertiary: shadows.md,
  },
  components: {
    // Layout 컴포넌트
    Layout: {
      headerBg: colors.background.dark,
      siderBg: colors.background.primary,
      bodyBg: colors.background.secondary,
    },
    
    // Menu 컴포넌트
    Menu: {
      itemBg: 'transparent',
      itemSelectedBg: colors.primary[50],
      itemSelectedColor: colors.primary[500],
      itemHoverBg: colors.gray[50],
      itemActiveBg: colors.primary[100],
    },
    
    // Button 컴포넌트
    Button: {
      borderRadius: radius.base,
      controlHeight: 32,
      controlHeightSM: 24,
      controlHeightLG: 40,
    },
    
    // Card 컴포넌트
    Card: {
      borderRadius: radius.md,
      boxShadow: shadows.card,
    },
    
    // Table 컴포넌트
    Table: {
      headerBg: colors.background.tertiary,
      borderColor: colors.border.secondary,
    },
    
    // Modal 컴포넌트
    Modal: {
      borderRadius: radius.lg,
      boxShadow: shadows.modal,
    },
  },
};

// CSS-in-JS 스타일 헬퍼 함수들
export const createResponsiveStyle = (styles: Record<keyof typeof breakpoints, any>) => {
  const mediaQueries: Record<string, string> = {};
  
  Object.entries(breakpoints).forEach(([key, value]) => {
    if (styles[key as keyof typeof breakpoints]) {
      mediaQueries[`@media (min-width: ${value}px)`] = styles[key as keyof typeof breakpoints];
    }
  });
  
  return mediaQueries;
};

export const createFlexCenter = () => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

export const createFlexBetween = () => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
});

export const createTruncateText = () => ({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap' as const,
});

export const createScrollbarStyle = () => ({
  '&::-webkit-scrollbar': {
    width: '6px',
  },
  '&::-webkit-scrollbar-track': {
    background: colors.gray[100],
    borderRadius: radius.full,
  },
  '&::-webkit-scrollbar-thumb': {
    background: colors.gray[300],
    borderRadius: radius.full,
    '&:hover': {
      background: colors.gray[400],
    },
  },
});

export default {
  colors,
  typography,
  spacing,
  breakpoints,
  shadows,
  radius,
  animations,
  antdTheme,
  createResponsiveStyle,
  createFlexCenter,
  createFlexBetween,
  createTruncateText,
  createScrollbarStyle,
};